import React, { useState, useEffect, useCallback } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Badge from "aldoo-ra/Badge"
import Tooltip from "aldoo-ra/Tooltip"
import Modal from "aldoo-ra/Modal"
import Checkbox from "aldoo-ra/Checkbox"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { renderPlatformIcon } from "../utils/platformIconUtils.jsx"
import PlatformSelector from "aldoo-ra/PlatformSelector"
import { api } from "aldoo-ra/API"
import moment from "moment"
import { showNotification } from "aldoo-ra/Notification"
import {
  DevicePhoneMobileIcon,
  PlusCircleIcon,
  TrashIcon,
  ArrowPathIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline"

// Device statistics overview component
const DeviceStatistics = ({ onRefresh }) => {
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    disabled: 0,
    deleted: 0,
    platforms: {}
  })
  const [loading, setLoading] = useState(true)

  // Initialize CRUD API
  const crudAPI = api("crud")

  const loadStatistics = useCallback(async () => {
    setLoading(true)
    try {
      // Use the proper user-device API endpoint with platform_devices command
      const deviceAPI = api("user-device")
      
      const response = await deviceAPI({
        platform_devices: {}
      })

      if (response.result && response.data) {
        setStats({
          total: response.data.total || 0,
          active: response.data.active || 0,
          disabled: response.data.disabled || 0,
          deleted: response.data.deleted || 0,
          platforms: response.data.platforms || {}
        })
      } else {
        // Fallback to default values if API fails
        setStats({
          total: 0,
          active: 0,
          disabled: 0,
          deleted: 0,
          platforms: {}
        })
      }
    } catch (error) {
      console.error("Failed to load device statistics:", error)
      // Set default values on error
      setStats({
        total: 0,
        active: 0,
        disabled: 0,
        deleted: 0,
        platforms: {}
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatistics()
  }, [loadStatistics, onRefresh])

  const StatCard = ({ title, value, icon: Icon, variant = "primary", showPercentage = true }) => {
    const getColorClasses = () => {
      switch (variant) {
        case "success":
          return "bg-admin_success text-white"
        case "warning":
          return "bg-admin_warning text-white"
        case "error":
          return "bg-admin_error text-white"
        default:
          return "bg-admin_primary_lt text-white"
      }
    }

    // Calculate percentage based on total devices
    const getPercentage = () => {
      if (loading || !showPercentage || stats.total === 0) return null
      const percentage = Math.round((value / stats.total) * 100)
      return `${percentage}%`
    }

    return (
      <div className="flex items-end justify-between bg-admin_paper border border-admin_bordered rounded-xl p-6">
        <div className="flex-1">
          <Typography variant="admin_txt1_dim" className="mb-2">
            {title}
          </Typography>

          <div className="flex items-baseline gap-3">
            <Typography 
              component="output"
              variant="admin_h2">
              {loading ? "..." : value.toLocaleString()}
            </Typography>

            {showPercentage && !loading && (
              <Typography variant="admin_txt1_dim">
                {getPercentage()}
              </Typography>
            )}
          </div>
        </div>

        <div className={`p-3 rounded-lg ${getColorClasses()}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    )
  }

  const PlatformBar = ({ platform, count, maxCount }) => {
    const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0
    const displayPercentage = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0

    // Use centralized platform icon utility

    return (
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-2 w-32">
          {renderPlatformIcon(platform, "w-5 h-5 text-admin_text_secondary")}

          <Typography variant="admin_txt1_dim">
            {platform}
          </Typography>
        </div>

        <div className="flex-1 relative">
          <div className="h-6 bg-admin_neutral_1 rounded-full overflow-hidden">
            <div
              className="h-full bg-admin_primary transition-all duration-300 ease-out"
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>

        <div className="flex items-center gap-2 w-20 text-right">
          <Typography 
            variant="admin_txt1" 
            className="text-admin_text font-medium"
          >
            {count}
          </Typography>

          <Typography variant="admin_txt2_dim">
            ({displayPercentage}%)
          </Typography>
        </div>
      </div>
    )
  }

  const platformEntries = Object.entries(stats.platforms)
  const maxPlatformCount = platformEntries.length > 0 ? Math.max(...Object.values(stats.platforms)) : 0

  return (
    <div className="space-y-6 mb-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Devices"
          value={stats.total}
          icon={DevicePhoneMobileIcon}
          variant="primary"
          showPercentage={false}
        />
        <StatCard
          title="Active Devices"
          value={stats.active}
          icon={UserGroupIcon}
          variant="success"
        />
        <StatCard
          title="Disabled Devices"
          value={stats.disabled}
          icon={ExclamationTriangleIcon}
          variant="warning"
        />
        <StatCard
          title="Deleted Devices"
          value={stats.deleted}
          icon={TrashIcon}
          variant="error"
        />
      </div>

      {/* Platform Distribution */}
      <div className="bg-admin_paper border border-admin_bordered rounded-xl p-6">
        <Typography 
          component="h2"
          variant="admin_h4" 
          className="mb-4"
        >
          Platform Distribution
        </Typography>

        {loading ? (
          <div className="flex justify-center py-8">
            <Typography variant="admin_txt1_dim">
              Loading distribution...
            </Typography>
          </div>
        ) : platformEntries.length === 0 ? (
          <div className="flex justify-center py-8">
            <Typography variant="admin_txt1_dim">
              No platform data available
            </Typography>
          </div>
        ) : (
          <div className="space-y-1">
            {platformEntries
              .sort(([,a], [,b]) => b - a) // Sort by count descending
              .map(([platform, count]) => (
                <PlatformBar
                  key={platform}
                  platform={platform}
                  count={count}
                  maxCount={maxPlatformCount}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  )
}

const PlatformDevices = () => {
  const [refreshKey, setRefreshKey] = useState(0)
  const [platformFilter, setPlatformFilter] = useState([{ value: "all", label: "All Platforms" }])
  const [restoreModal, setRestoreModal] = useState(false)
  const [selectedDevices, setSelectedDevices] = useState([])
  const [selectedDeviceData, setSelectedDeviceData] = useState([])
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, deviceCount: 0, isBulk: false, onConfirm: null })

  // Initialize CRUD API
  const crudAPI = api("crud")

  // Refresh when platform filter changes
  useEffect(() => {
    setRefreshKey((prev) => prev + 1)
  }, [platformFilter])

  // API method to get all devices
  const getAllDevices = async (requestFields = {}) => {
    try {
      // Build platform filter
      const platformMatch = {}
      if (platformFilter.length > 0 && !platformFilter.some(f => f.value === "all")) {
        platformMatch.platform = { $in: platformFilter.map(f => f.value) }
      }

      // Query all devices using the CRUD API
      const fields = {
        ...requestFields,
        model: "UserDevice",
        match: { 
          deleted: { $ne: true }, // Only show non-deleted devices
          ...platformMatch,
          ...requestFields.match
        },
        sort: { created: -1 },
      }

      return await crudAPI({ get: fields })
    } catch (error) {
      console.error("Failed to fetch devices:", error)
      return {
        result: false,
        data: [],
        totalItems: 0
      }
    }
  }

  // API method to get deleted devices
  const getDeletedDevices = async (requestFields = {}) => {
    try {
      // Build platform filter
      const platformMatch = {}
      if (platformFilter.length > 0 && !platformFilter.some(f => f.value === "all")) {
        platformMatch.platform = { $in: platformFilter.map(f => f.value) }
      }

      // Query deleted devices using the CRUD API
      const fields = {
        ...requestFields,
        model: "UserDevice",
        match: { 
          deleted: true, // Only show deleted devices
          ...platformMatch,
          ...requestFields.match
        },
        sort: { created: -1 },
      }

      return await crudAPI({ get: fields })
    } catch (error) {
      console.error("Failed to fetch deleted devices:", error)
      return {
        result: false,
        data: [],
        totalItems: 0
      }
    }
  }

  // API method to restore a device
  const restoreDevice = async (deviceId) => {
    try {
      return await crudAPI({
        update: {
          model: "UserDevice",
          match: { _id: deviceId },
          data: { deleted: null } // Remove the deleted field by setting to null
        }
      })
    } catch (error) {
      console.error("Failed to restore device:", error)
      return { result: false, error: error.message }
    }
  }

  // API method to restore multiple devices
  const restoreDevices = async (deviceIds) => {
    try {
      // For multiple devices, we'll need to restore them individually
      // since the CRUD format doesn't seem to support bulk operations with $in
      const results = await Promise.all(
        deviceIds.map(deviceId => restoreDevice(deviceId))
      )
      
      return { result: results.every(r => r.result !== false) }
    } catch (error) {
      console.error("Failed to restore devices:", error)
      return { result: false, error: error.message }
    }
  }

  // API method to permanently delete a device
  const deleteDeviceForever = async (deviceId) => {
    try {
      return await crudAPI({
        delete: {
          model: "UserDevice",
          match: { _id: deviceId }
        }
      })
    } catch (error) {
      console.error("Failed to delete device forever:", error)
      return { result: false, error: error.message }
    }
  }

  // Helper functions
  const formatDate = (date) => {
    if (!date) return "Never"
    try {
      const dateObj = date instanceof Date ? date : new Date(date)
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return "Invalid date"
    }
  }

  const getTimeAgo = (date) => {
    if (!date) return "Never"
    try {
      return moment(date).fromNow()
    } catch (error) {
      return "Invalid date"
    }
  }

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return "Unknown Model"
    
    // Check if it's a user agent string (contains Mozilla)
    if (!userAgent.includes("Mozilla")) {
      return userAgent // Return as-is if it's not a user agent
    }

    try {
      let browser = "Unknown Browser"
      let os = "Unknown OS"

      // Extract browser
      if (userAgent.includes("Chrome")) {
        browser = "Chrome"
      } else if (userAgent.includes("Firefox")) {
        browser = "Firefox"
      } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
        browser = "Safari"
      } else if (userAgent.includes("Edge")) {
        browser = "Edge"
      } else if (userAgent.includes("Mozilla")) {
        browser = "Mozilla"
      }

      // Extract OS
      if (userAgent.includes("Macintosh") || userAgent.includes("Mac OS X")) {
        os = "macOS"
      } else if (userAgent.includes("Windows")) {
        os = "Windows"
      } else if (userAgent.includes("Linux")) {
        os = "Linux"
      } else if (userAgent.includes("Android")) {
        os = "Android"
      } else if (userAgent.includes("iPhone") || userAgent.includes("iPad")) {
        os = "iOS"
      }

      return `${browser} • ${os}`
    } catch (error) {
      return userAgent
    }
  }

  const getStatusBadge = (device) => {
    if (!device.enabled) return <Badge variant="admin_error">Disabled</Badge>
    if (device.online) return <Badge variant="admin_success">Online</Badge>
    if (device.login) return <Badge variant="admin_info">Logged In</Badge>
    return <Badge variant="admin_warning">Offline</Badge>
  }
  const handleAddDevice = () => {
    console.log("Add device clicked")
  }

  const handleViewDevice = (device) => {
    console.log("View device:", device)
  }

  // Selection handlers
  const handleSelectDevice = (device, isSelected) => {
    if (isSelected) {
      setSelectedDevices([...selectedDevices, device._id])
      setSelectedDeviceData([...selectedDeviceData, device])
    } else {
      setSelectedDevices(selectedDevices.filter(id => id !== device._id))
      setSelectedDeviceData(selectedDeviceData.filter(d => d._id !== device._id))
    }
  }

  const handleSelectAll = (devices, isSelected) => {
    if (isSelected) {
      setSelectedDevices(devices.map(d => d._id))
      setSelectedDeviceData([...devices])
    } else {
      setSelectedDevices([])
      setSelectedDeviceData([])
    }
  }

  const handleDeleteDevice = (device) => {
    setDeleteConfirmModal({
      isOpen: true,
      deviceCount: 1,
      isBulk: false,
      onConfirm: async () => {
        try {
          const response = await crudAPI({
            update: {
              model: "UserDevice",
              match: { _id: device._id },
              data: { deleted: true }
            }
          })
          
          if (response.result !== false) {
            setRefreshKey((prev) => prev + 1)
          }
        } catch (error) {
          console.error("Failed to delete device:", error)
        }
        setDeleteConfirmModal({ isOpen: false, deviceCount: 0, isBulk: false, onConfirm: null })
      }
    })
  }

  const handleBulkDeleteDevices = () => {
    setDeleteConfirmModal({
      isOpen: true,
      deviceCount: selectedDevices.length,
      isBulk: true,
      onConfirm: async () => {
        try {
          // Delete devices individually since the CRUD format doesn't support bulk operations with $in
          const results = await Promise.all(
            selectedDevices.map(deviceId => 
              crudAPI({
                update: {
                  model: "UserDevice",
                  match: { _id: deviceId },
                  data: { deleted: true }
                }
              })
            )
          )
          
          if (results.every(r => r.result !== false)) {
            setRefreshKey((prev) => prev + 1)
            setSelectedDevices([])
            setSelectedDeviceData([])
          }
        } catch (error) {
          console.error("Failed to delete devices:", error)
        }
        setDeleteConfirmModal({ isOpen: false, deviceCount: 0, isBulk: false, onConfirm: null })
      }
    })
  }

  // Header controls
  const headerControls = (
    <div className="w-full flex items-end">
      {/* Platform Filter */}
      <div className="flex flex-col">
        <Typography 
          variant="admin_txt2_dim" 
          className="mb-0.5"
        >
          Platform
        </Typography>

        <PlatformSelector
          platforms={[
            { value: "all", label: "All Platforms" },
            { value: "iOS", label: "iOS" },
            { value: "Android", label: "Android" },
            { value: "Windows", label: "Windows" },
            { value: "Mac", label: "Mac" },
            { value: "Linux", label: "Linux" },
            { value: "Web", label: "Web" },
          ]}
          value={platformFilter}
          onChange={(values) => setPlatformFilter(values?.length ? values : [{ value: "all", label: "All Platforms" }])}
          fixedWidth="150px"
        />
      </div>
    </div>
  )

  return (
    <div className="p-6">
      {/* Device Statistics Overview */}
      <DeviceStatistics onRefresh={refreshKey} />

      <div className="w-full flex items-center justify-between mb-5">
        <Typography 
          component="h1"
          variant="admin_h3"
        >
          Platform Devices
        </Typography>

        <div className="flex items-end justify-end gap-3">
          <Button 
            variant="admin_secondary" 
            onClick={() => setRestoreModal(true)}
          >
            <ArrowPathIcon className="w-4 h-4 mr-1" />
            Restore
          </Button>

          <Button 
            variant="admin_primary" 
            onClick={handleAddDevice}
          >
            <PlusCircleIcon className="w-5 h-5 mr-1" />
            Add
          </Button>
        </div>
      </div>
      
      <DataTable
        variant="admin_browser"
        apiMethod={getAllDevices}
        refreshKey={refreshKey}
        requestFields={{}}
        headerControls={headerControls}
        searchField="name model platform deviceID ip"
        itemsPerPage={25}
        preferredHeight="75vh"
        itemHeight="h-[70px]"
        enableSelection={true}
        selectedItems={selectedDevices}
        selectedData={selectedDeviceData}
        onSelectionChange={(ids, data) => {
          setSelectedDevices(ids);
          setSelectedDeviceData(data);
        }}
        bulkActions={[
          {
            label: "Delete",
            variant: "admin_error",
            icon: <TrashIcon className="w-4 h-4" />,
            onClick: handleBulkDeleteDevices,
          }
        ]}
        itemIdField="_id"
        localUserPreferencesKey="admin.platformAccess.devices"
        styleOverrides={{
          table: "w-full table-fixed",
          header: "w-full flex justify-between items-end gap-2 -mt-5 mb-4",
          container: "relative w-full flex flex-col h-[90vh] justify-start bg-admin_paper rounded-3xl px-6 pt-8 pb-6",
          searchInput: "absolute w-[350px] h-[30px] top-6 right-4 text-text bg-transparent outline-none flex-1 px-2 py-1",
        }}
        renders={[
          {
            id: "device",
            title: "Device",
            headerClassName: "w-[180px] xl:w-[30%]",
            render: (_, device) => (
              <div className="flex items-center gap-3 overflow-hidden">
                {renderPlatformIcon(device.platform, "hidden xl:block w-8 h-8 text-admin_text_secondary flex-shrink-0")}
                
                <div className="min-w-0 flex-1 overflow-hidden">
                  <Typography 
                    variant="admin_label" 
                    className="truncate my-0 block"
                  >
                    {device.name || device.deviceName || "Unknown Device"}
                  </Typography>

                  <div className="flex items-center gap-1 xl:gap-2 overflow-hidden">
                    <Tooltip display="block w-fit" content={device.model || "Unknown Model"}>
                      <Typography 
                        variant="admin_txt1_dim" 
                        className="w-fit truncate flex-shrink-0 block"
                      >
                        {parseUserAgent(device.model)}
                      </Typography>
                    </Tooltip>

                    <span className="flex-shrink-0 text-admin_text_secondary">•</span>

                    <Typography 
                      variant="admin_txt1_dim" 
                      className="flex-shrink-0 truncate block max-w-[80px]"
                    >
                      {device.platform || "Unknown"}
                    </Typography>

                    {device.deviceID && (
                      <>
                        <span className="flex-shrink-0 text-admin_text_secondary">•</span>

                        <Tooltip content={
                          <div className="text-center">
                            <Typography 
                              variant="admin_txt1"
                              className="block"
                            >
                              {device.deviceID}
                            </Typography>
      
                            <Typography variant="admin_txt2_dim">
                              Click to copy
                            </Typography>
                          </div>
                        }>
                          <span className="inline-block max-w-[40px] xl:max-w-[120px]">
                            <button 
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(device.deviceID)
                                  showNotification({
                                    message: "Copied to clipboard",
                                    className: "bg-admin_primary text-white"
                                  })
                                } catch (error) {
                                  console.error("Failed to copy:", error)
                                }
                              }}
                              className="font-mono truncate text-admin_text_secondary hover:text-admin_primary transition-colors text-left block w-full"
                            >
                              {device.deviceID}
                            </button>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: "status",
            title: "Status",
            headerClassName: "w-[40px] xl:w-[60px]",
            render: (_, device) => getStatusBadge(device),
          },
          {
            id: "ip",
            title: "IP",
            headerClassName: "w-[60px] xl:w-[100px]",
            render: (_, device) => (
              <Tooltip display="block" content={
                <div className="text-center">
                  <Typography 
                    variant="admin_txt1"
                    className="block"
                  >
                    {device.ip}
                  </Typography>

                  <Typography variant="admin_txt2_dim" className="block">
                    Click to copy
                  </Typography>
                </div>
              }>
                <Typography 
                  variant="admin_txt1" 
                  className="block text-admin_text font-mono"
                >
                  <button 
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(device.ip)
                        showNotification({
                          message: "Copied to clipboard",
                          className: "bg-admin_primary text-white"
                        })
                      } catch (error) {
                        console.error("Failed to copy:", error)
                      }
                    }}
                    className="w-full max-w-[80px] xl:max-w-[160px] font-mono truncate whitespace-nowrap text-admin_text hover:text-admin_primary transition-colors text-left"
                  >
                    {device.ip || "N/A"}
                  </button>
                </Typography>
              </Tooltip>
            ),
          },
          {
            id: "lastActivity",
            title: "Last Activity",
            headerClassName: "w-[130px] xl:w-[150px] whitespace-nowrap",
            render: (_, device) => (
              <div className="w-fit text-sm">
                <div className="w-fit flex gap-1 xl:flex-nowrap mb-1">
                  <span className="text-admin_text_secondary">
                    Login: 
                  </span>

                  <Tooltip display="block w-fit" content={getTimeAgo(device.lastLogin)}>
                    <span className="text-admin_text whitespace-nowrap cursor-help">
                      {formatDate(device.lastLogin)}
                    </span>
                  </Tooltip>
                </div>
                
                <div className="flex gap-1 xl:flex-nowrap">
                  <span className="text-admin_text_secondary">
                    Ping: 
                  </span>

                  <Tooltip content={getTimeAgo(device.lastPing)}>
                    <span className="text-admin_text cursor-help">
                      {formatDate(device.lastPing)}
                    </span>
                  </Tooltip>
                </div>
              </div>
            ),
          },
          {
            id: "created",
            title: "Created",
            headerClassName: "w-[70px] xl:w-[110px]",
            render: (_, device) => (
              <Tooltip content={getTimeAgo(device.created)}>
                <Typography 
                  variant="admin_txt1" 
                  className="text-admin_text_secondary cursor-help"
                >
                  {formatDate(device.created)}
                </Typography>
              </Tooltip>
            ),
          },
          {
            id: "actions",
            title: "Actions",
            headerClassName: "w-10 text-center",
            render: (_, device) => (
              <div className="flex items-center justify-center">
                <button
                  className="rounded hover:bg-admin_neutral_1/50 text-admin_text_secondary hover:text-admin_error transition-colors p-1.5"
                  onClick={() => handleDeleteDevice(device)}
                  title="Remove device"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ),
          },
        ]}
        noData={
          <div className="text-center py-12">
            <DevicePhoneMobileIcon className="w-16 h-16 text-admin_text_secondary mx-auto mb-4" />
            <Typography variant="admin_h4" className="text-admin_text_secondary mb-2">
              No devices found
            </Typography>
            <Typography variant="admin_txt1" className="text-admin_text_secondary">
              No devices are currently registered on the platform
            </Typography>
          </div>
        }
      />

      {/* Restore devices modal */}
      <RestoreDevicesModal
        isOpen={restoreModal}
        onClose={() => setRestoreModal(false)}
        onRestore={() => setRefreshKey(prev => prev + 1)}
        platformFilter={platformFilter}
      />

      {/* Delete confirmation modal */}
      <DeleteConfirmationModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={() => setDeleteConfirmModal({ isOpen: false, deviceCount: 0, isBulk: false, onConfirm: null })}
        onConfirm={deleteConfirmModal.onConfirm}
        deviceCount={deleteConfirmModal.deviceCount}
        isBulk={deleteConfirmModal.isBulk}
      />
    </div>
  )
}

// Restore deleted devices modal
const RestoreDevicesModal = ({ isOpen, onClose, onRestore, platformFilter }) => {
  const [refreshKey, setRefreshKey] = useState(0)

  // Initialize CRUD API
  const crudAPI = api("crud")

  // Refresh the table when dialog opens
  useEffect(() => {
    if (!isOpen) return
    setRefreshKey((prev) => prev + 1)
  }, [isOpen])

  // API method to get deleted devices
  const getDeletedDevices = async (requestFields = {}) => {
    try {
      // Build platform filter
      const platformMatch = {}
      if (platformFilter.length > 0 && !platformFilter.some(f => f.value === "all")) {
        platformMatch.platform = { $in: platformFilter.map(f => f.value) }
      }

      // Query deleted devices using the CRUD API
      const fields = {
        ...requestFields,
        model: "UserDevice",
        match: { 
          deleted: true, // Only show deleted devices
          ...platformMatch,
          ...requestFields.match
        },
        sort: { created: -1 },
      }

      return await crudAPI({ get: fields })
    } catch (error) {
      console.error("Failed to fetch deleted devices:", error)
      return {
        result: false,
        data: [],
        totalItems: 0
      }
    }
  }

  // Action handlers
  const restoreAction = async (device) => {
    try {
      const response = await crudAPI({
        update: {
          model: "UserDevice",
          match: { _id: device._id },
          data: { deleted: null } // Remove the deleted field by setting to null
        }
      })
      
      if (response.result !== false) {
        setRefreshKey((prev) => prev + 1)
        onRestore()
      }
    } catch (error) {
      console.error("Failed to restore device:", error)
    }
  }

  const deleteForeverAction = (device) => {
    const deviceName = device.name || device.deviceName || "Unknown Device"
    
    showPromptDialog({
      title: "Confirm Permanent Delete",
      message: `Are you sure you want to permanently delete "${deviceName}"? This action cannot be undone.`,
      confirmText: "Delete Forever",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const response = await crudAPI({
            delete: {
              model: "UserDevice",
              match: { _id: device._id }
            }
          })
          
          if (response.result !== false) {
            setRefreshKey((prev) => prev + 1)
          }
        } catch (error) {
          console.error("Failed to delete device forever:", error)
        }
      }
    })
  }

  // Helper functions
  const formatDate = (date) => {
    if (!date) return "Never"
    try {
      const dateObj = date instanceof Date ? date : new Date(date)
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      return "Invalid date"
    }
  }

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return "Unknown Model"
    
    // Check if it's a user agent string (contains Mozilla)
    if (!userAgent.includes("Mozilla")) {
      return userAgent // Return as-is if it's not a user agent
    }

    try {
      let browser = "Unknown Browser"
      let os = "Unknown OS"

      // Extract browser
      if (userAgent.includes("Chrome")) {
        browser = "Chrome"
      } else if (userAgent.includes("Firefox")) {
        browser = "Firefox"
      } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
        browser = "Safari"
      } else if (userAgent.includes("Edge")) {
        browser = "Edge"
      } else if (userAgent.includes("Mozilla")) {
        browser = "Mozilla"
      }

      // Extract OS
      if (userAgent.includes("Macintosh") || userAgent.includes("Mac OS X")) {
        os = "macOS"
      } else if (userAgent.includes("Windows")) {
        os = "Windows"
      } else if (userAgent.includes("Linux")) {
        os = "Linux"
      } else if (userAgent.includes("Android")) {
        os = "Android"
      } else if (userAgent.includes("iPhone") || userAgent.includes("iPad")) {
        os = "iOS"
      }

      return `${browser} • ${os}`
    } catch (error) {
      return userAgent
    }
  }

  // Define columns for the restore devices table
  const tableItemRender = [
    {
      id: "device",
      title: "Device",
      headerClassName: "w-[300px]",
      render: (_, device) => (
        <div className="flex items-center space-x-3">
          {renderPlatformIcon(device.platform, "w-8 h-8 text-admin_text_secondary")}
          <div className="min-w-0 flex-1">
            <Typography variant="admin_body" className="font-medium truncate">
              {device.name || device.deviceName || "Unknown Device"}
            </Typography>
            <div className="flex items-center gap-2">
              <Tooltip content={device.model || "Unknown Model"}>
                <Typography variant="admin_txt1" className="text-admin_text_secondary">
                  {parseUserAgent(device.model)}
                </Typography>
              </Tooltip>
              <span className="text-admin_text_secondary">•</span>
              <Typography variant="admin_txt1" className="text-admin_text_secondary capitalize">
                {device.platform || "Unknown"}
              </Typography>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "ip",
      title: "IP",
      headerClassName: "w-[120px]",
      render: (_, device) => (
        <Typography variant="admin_txt1" className="text-admin_text font-mono">
          {device.ip || "N/A"}
        </Typography>
      ),
    },
    {
      id: "created",
      title: "Created",
      headerClassName: "w-[120px]",
      render: (_, device) => (
        <Typography variant="admin_txt1" className="text-admin_text_secondary">
          {formatDate(device.created)}
        </Typography>
      ),
    },
    {
      id: "restore",
      title: "Restore",
      headerClassName: "w-[100px]",
      render: (_, device) => (
        <Button
          variant="admin_secondary"
          className="w-20"
          onClick={() => restoreAction(device)}
        >
          Restore
        </Button>
      ),
    },
    {
      id: "delete",
      title: "Delete",
      headerClassName: "w-[120px]",
      render: (_, device) => (
        <Button
          variant="admin_secondary"
          className="hover:bg-admin_error hover:text-white whitespace-nowrap"
          onClick={() => deleteForeverAction(device)}
        >
          Delete Forever
        </Button>
      ),
    },
  ]

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape
      className="md:absolute w-full md:w-[90%] h-full md:h-auto top-[15px]"
      zIndex={200}
    >
      <div className="p-4">
        <Typography variant="admin_h3" className="mb-4">
          Restore Deleted Devices
        </Typography>

        {/* Data table for displaying deleted devices */}
        <div className="h-[65vh]">
          <DataTable
            localUserPreferencesKey="admin.platformAccess.devicesRestore"
            variant="admin"
            refreshKey={refreshKey}
            apiMethod={getDeletedDevices}
            preferredHeight="65vh"
            itemHeight="h-[80px]"
            styleOverrides={{
              searchInput: "absolute top-[45px] right-[40px] w-full max-w-[300px] xl:max-w-[360px] ml-6 xl:ml-8"
            }}
            searchField="name model platform deviceID ip"
            searchObject={{}}
            sort={{ created: -1 }}
            renders={tableItemRender}
            noData={
              <div className="min-h-[400px] flex items-center justify-center">
                <Typography variant="admin_body" className="text-admin_text_secondary">
                  No deleted devices found
                </Typography>
              </div>
            }
          />
        </div>
      </div>
    </Modal>
  )
}

// Confirmation modal for delete actions
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, deviceCount, isBulk = false }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_centered"
      title="Confirm Deletion"
      className="max-w-md"
    >
      <div className="space-y-4">
        <Typography variant="admin_body">
          {isBulk
            ? `Are you sure you want to delete ${deviceCount} selected device${deviceCount !== 1 ? 's' : ''}?`
            : `Are you sure you want to delete this device?`
          }
        </Typography>
        <Typography variant="admin_txt1" className="text-admin_text_secondary">
          This action will soft-delete the device{deviceCount !== 1 ? 's' : ''} and can be undone using the restore function.
        </Typography>
        <div className="flex justify-end gap-2 pt-4 border-t border-admin_bordered">
          <Button variant="admin_secondary" onClick={onClose}>Cancel</Button>
          <Button variant="admin_error" onClick={onConfirm}>
            <TrashIcon className="w-4 h-4 mr-1" />
            Delete {isBulk ? `${deviceCount} Device${deviceCount !== 1 ? 's' : ''}` : 'Device'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default PlatformDevices